import type { CodingJobState } from '../types';

export const CODING_STAGES = [
  'Discussing',
  'Send approval',
  'Planning',
  'Plan approval',
  'Building',
  'Review',
  'Pull request',
  'Merged',
  'Live',
] as const;

export type CodingMood = 'discussing' | 'waiting' | 'working' | 'done' | 'stopped';

export interface CodingStatus {
  stage: number;
  mood: CodingMood;
  headline: string;
}

const STATUS: Record<CodingJobState, CodingStatus> = {
  draft: { stage: 0, mood: 'discussing', headline: 'Still being discussed in staff chat' },
  awaiting_send: { stage: 1, mood: 'waiting', headline: 'Waiting for approval to send' },
  send_declined: { stage: 1, mood: 'stopped', headline: 'Send was declined' },
  approved: { stage: 2, mood: 'working', headline: 'Starting the coding agent' },
  planning: { stage: 2, mood: 'working', headline: 'The coding agent is writing a plan' },
  awaiting_plan: { stage: 3, mood: 'waiting', headline: 'Plan is waiting for approval' },
  plan_declined: { stage: 3, mood: 'stopped', headline: 'Plan was declined' },
  plan_approved: { stage: 4, mood: 'waiting', headline: 'Plan approved, waiting to build' },
  building: { stage: 4, mood: 'working', headline: 'The coding agent is building and testing' },
  fixing: { stage: 4, mood: 'working', headline: 'The coding agent is fixing review findings' },
  awaiting_budget: { stage: 4, mood: 'waiting', headline: 'Paused at the budget check-in' },
  reviewing: { stage: 5, mood: 'working', headline: 'The reviewer is checking the work' },
  needs_you: { stage: 5, mood: 'waiting', headline: 'The job needs you' },
  pr_ready: { stage: 6, mood: 'waiting', headline: 'Pull request is ready to review and merge' },
  pr_closed: { stage: 6, mood: 'stopped', headline: 'Pull request was closed without merging' },
  merged: { stage: 7, mood: 'waiting', headline: 'Merged and on staging, waiting for the production release' },
  live: { stage: 8, mood: 'done', headline: 'Live in production' },
  failed: { stage: 4, mood: 'stopped', headline: 'The job failed' },
  cancelled: { stage: 4, mood: 'stopped', headline: 'The job was cancelled' },
};

const SWITCHED_OFF: CodingStatus = {
  stage: 2,
  mood: 'stopped',
  headline: 'Waiting: the coding agent is switched off',
};

export function codingStatus(state: CodingJobState, agentAvailable = true): CodingStatus {
  if (state === 'approved' && !agentAvailable) return SWITCHED_OFF;
  return STATUS[state];
}

/** Waiting on a super admin: the viewer sees "your approval" only if they can give it. */
export function waitingLabel(state: CodingJobState, canDecide: boolean, agentAvailable = true): string {
  const base = codingStatus(state, agentAvailable);
  if (base.mood !== 'waiting') return base.headline;
  if (state === 'plan_approved' || state === 'merged') return base.headline;
  return canDecide ? `${base.headline} (yours)` : `${base.headline} from a super admin`;
}
